/**
 * Investor-Portal — Szenario-Simulator (#1186 v2.570.0 · Transparenz #1192 v2.574.0 · Sprungfix #1194 v2.576.0).
 *
 * Phase-2-Logik (#1194):
 *   - Outbound + Inbound = SPRUNGFIX (Personal): zwei Volumen-Regler AT/DE,
 *     getrennte Aufwands-Sub-Klassifikation (personal_at, finecom_de, normal_var, normal_fix).
 *     Personal-Aufwand skaliert NICHT linear, sondern in 20h/Woche-Stufen (= 0,5 VZÄ).
 *     Modell-Annahmen kommen aus system_config['scenario_personnel_model'] (Annahmen-Page).
 *   - Lager / Carrier / Material = CLASSIC (linear var × Volumen × (2-Effizienz)).
 *   - Effizienz hat in Sprungfix-Mode neue Semantik: erhöht die Kapazität der bestehenden
 *     Belegschaft (vorhanden = basis × effizienz) → weniger neue Stufen nötig.
 *
 *   Rechen-Logik Sprungfix:
 *     benötigt = basis_kapazitaet × volumen_at
 *     vorhanden = basis_kapazitaet × effizienz
 *     stufen = max(0, ceil((benötigt - vorhanden) / kapazitaet_pro_stufe))
 *     personalkosten_zusatz = stufen × kosten_pro_stufe_eur
 *     personal_at_neu = personal_at_alt − personalkosten_zusatz   (Aufwand wird negativer)
 *     finecom_de_neu  = finecom_de_alt × volumen_de
 *     normal_var_neu  = normal_var_alt × volumen_at
 *     normal_fix_neu  = normal_fix_alt
 *
 *   Reconciliation: alle Regler neutral → stufen=0 → personalkosten_zusatz=0 → keine Drift.
 *
 *   Rechen-Logik Classic:
 *     Umsatz_neu  = Umsatz × Volumen × Preis
 *     Aufw_neu    = Aufw_fix + (Aufw_variabel × Volumen × (2 - Effizienz))
 *
 *   Gemein_neu = Gemein × (1 − Reduktion)
 *   EBITDA_neu = Σ DB_neu + Gemein_neu
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ComposedChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, LabelList,
} from 'recharts';
import {
  Sliders, RotateCcw, TrendingUp, TrendingDown, Wallet, Calculator, Settings,
  Info, ChevronDown, ChevronUp, Users,
} from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const API = import.meta.env.VITE_API_URL || '';

function fmtEur(v)        { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(v); }
function fmtEurDetail(v)  { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v); }
function fmtNum(v)        { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{maximumFractionDigits:0}).format(v); }
function fmtPct(v)        { if (v==null||isNaN(v)) return '–'; return `${v.toFixed(1)} %`; }
function fmtSignedEur(v)  { if (v==null||isNaN(v)) return '–'; const s = v>=0?'+':''; return s + fmtEur(v); }
function fmtMult(v)       { return `${v.toFixed(2)}×`; }

const CLASSIC_DEFAULT_LEVERS = { volumen: 1.0, preis: 1.0, effizienz: 1.0 };
const SPRUNGFIX_DEFAULT_LEVERS = { volumen_at: 1.0, volumen_de: 1.0, preis: 1.0, effizienz: 1.0 };
const GEMEIN_DEFAULT_REDUKTION = 0.0;

function defaultLeversForSegment(seg) {
  return seg.sprungfix_enabled ? { ...SPRUNGFIX_DEFAULT_LEVERS } : { ...CLASSIC_DEFAULT_LEVERS };
}
function buildEmptyLevers(segments) {
  const out = {};
  for (const s of segments) out[s.code] = defaultLeversForSegment(s);
  return out;
}
function isLeverNeutral(lev, sprungfix) {
  if (sprungfix) {
    return (lev.volumen_at ?? 1) === 1 && (lev.volumen_de ?? 1) === 1
        && (lev.preis ?? 1) === 1 && (lev.effizienz ?? 1) === 1;
  }
  return (lev.volumen ?? 1) === 1 && (lev.preis ?? 1) === 1 && (lev.effizienz ?? 1) === 1;
}

/**
 * #1194 — Sprungfix-Hebel-Logik für Outbound + Inbound.
 * Reconciliation: alle Regler neutral → stufen=0 → personalkosten_zusatz=0 → keine Drift.
 */
function applySprungfixLevers(seg, lev, personnelConfig) {
  const volAt = lev.volumen_at ?? 1;
  const volDe = lev.volumen_de ?? 1;
  const pr    = lev.preis ?? 1;
  const eff   = lev.effizienz ?? 1;

  const umsatzAtAlt = seg.umsatz_at || 0;
  const umsatzDeAlt = seg.umsatz_de || 0;
  const umsatzAlt   = seg.umsatz || (umsatzAtAlt + umsatzDeAlt);

  const personalAtAlt = seg.aufwand_personal_at || 0;
  const finecomDeAlt  = seg.aufwand_finecom_de  || 0;
  const normalVarAlt  = seg.aufwand_normal_var  || 0;
  const normalFixAlt  = seg.aufwand_normal_fix  || 0;

  // Umsatz
  const umsatzAtNeu = umsatzAtAlt * volAt * pr;
  const umsatzDeNeu = umsatzDeAlt * volDe * pr;
  const umsatzNeu   = umsatzAtNeu + umsatzDeNeu;

  // Sprungfix-Personal-Stufenrechnung
  const basisKap    = personnelConfig?.basis_kapazitaet      || 0;
  const kapStufe    = personnelConfig?.kapazitaet_pro_stufe  || 1;
  const kostenStufe = personnelConfig?.kosten_pro_stufe_eur  || 0;
  const benoetigt   = basisKap * volAt;
  const vorhanden   = basisKap * eff;  // eff=1.0 → keine zusätzl. Kapazität; eff=1.5 → +50%
  const stufen      = (basisKap > 0 && kapStufe > 0)
                        ? Math.max(0, Math.ceil((benoetigt - vorhanden) / kapStufe))
                        : 0;
  const personalkostenZusatz = stufen * kostenStufe;
  const personalAtNeu = personalAtAlt - personalkostenZusatz; // Aufwand ist negativ → mehr negativ

  // Finecom DE = linear × volumen_de
  const finecomDeNeu = finecomDeAlt * volDe;
  // Normal var = linear × volumen_at (operative AT-Variable)
  const normalVarNeu = normalVarAlt * volAt;
  // Normal fix = immun
  const normalFixNeu = normalFixAlt;

  const aufwAlt = personalAtAlt + finecomDeAlt + normalVarAlt + normalFixAlt;
  const aufwNeu = personalAtNeu + finecomDeNeu + normalVarNeu + normalFixNeu;
  const dbAlt   = umsatzAlt + aufwAlt;
  const dbNeu   = umsatzNeu + aufwNeu;

  return {
    umsatz_neu: umsatzNeu,
    umsatz_at_neu: umsatzAtNeu,
    umsatz_de_neu: umsatzDeNeu,
    fix_neu: normalFixNeu,
    var_neu: personalAtNeu + finecomDeNeu + normalVarNeu,
    aufw_neu: aufwNeu,
    db_neu: dbNeu,
    deckungsgrad_neu: umsatzNeu !== 0 ? -aufwNeu / umsatzNeu * 100 : null,
    db_marge_neu: umsatzNeu !== 0 ? dbNeu / umsatzNeu * 100 : null,
    sprungfix: {
      stufen,
      personalkosten_zusatz: personalkostenZusatz,
      benoetigt,
      vorhanden,
      basis_kapazitaet: basisKap,
      kapazitaet_pro_stufe: kapStufe,
      kosten_pro_stufe_eur: kostenStufe,
      einheit: personnelConfig?.einheit || '',
      personal_at_alt: personalAtAlt, personal_at_neu: personalAtNeu,
      finecom_de_alt:  finecomDeAlt,  finecom_de_neu:  finecomDeNeu,
      normal_var_alt:  normalVarAlt,  normal_var_neu:  normalVarNeu,
      normal_fix_alt:  normalFixAlt,  normal_fix_neu:  normalFixNeu,
      umsatz_at_alt:   umsatzAtAlt,   umsatz_de_alt:   umsatzDeAlt,
    },
  };
}

/** Classic-Hebel-Logik (Lager / Carrier / Material). */
function applyClassicLevers(seg, lev) {
  const vol = lev.volumen ?? 1;
  const pr  = lev.preis ?? 1;
  const eff = lev.effizienz ?? 1;
  const effFactor = Math.max(0, 2 - eff);
  const umsatz_neu = seg.umsatz * vol * pr;
  const fix_neu = seg.aufwand_fix;
  const var_neu = seg.aufwand_variabel * vol * effFactor;
  const aufw_neu = fix_neu + var_neu;
  const db_neu = umsatz_neu + aufw_neu;
  return {
    umsatz_neu, fix_neu, var_neu, aufw_neu, db_neu,
    deckungsgrad_neu: umsatz_neu !== 0 ? -aufw_neu / umsatz_neu * 100 : null,
    db_marge_neu: umsatz_neu !== 0 ? db_neu / umsatz_neu * 100 : null,
  };
}

/** Wendet Hebel je nach Segment-Typ an. */
function applyLevers(seg, lev, personnelConfig) {
  return seg.sprungfix_enabled
    ? applySprungfixLevers(seg, lev, personnelConfig)
    : applyClassicLevers(seg, lev);
}

/**
 * #1192/#1194 — Pro Bucket Szenario-Wert berechnen.
 *   Sprungfix-Segment: nutzt subclassification (personal_at/finecom_de/normal_var/normal_fix).
 *     Personal-Buckets werden anteilig auf personal_at_neu verteilt.
 *   Classic-Segment: alte Logik (variabel × vol × (2-eff)).
 */
function applyLeversToBuckets(buckets, lev, segMeta) {
  if (!Array.isArray(buckets)) return [];
  if (segMeta?.sprungfix_enabled) {
    const personalAtAlt = segMeta.aufwand_personal_at || 0;
    const stufen        = segMeta.calc?.sprungfix?.stufen || 0;
    const kostenStufe   = segMeta.calc?.sprungfix?.kosten_pro_stufe_eur || 0;
    const personalkostenZusatz = stufen * kostenStufe;
    const personalAtNeu = personalAtAlt - personalkostenZusatz;
    const volAt = lev.volumen_at ?? 1;
    const volDe = lev.volumen_de ?? 1;
    return buckets.map(b => {
      let amount_neu = b.amount;
      if (b.subclassification === 'personal_at') {
        const share = personalAtAlt !== 0 ? (b.amount / personalAtAlt) : 0;
        amount_neu = personalAtNeu * share;
      } else if (b.subclassification === 'finecom_de') {
        amount_neu = b.amount * volDe;
      } else if (b.subclassification === 'normal_var') {
        amount_neu = b.amount * volAt;
      } else {
        amount_neu = b.amount; // normal_fix oder invoice
      }
      return {
        ...b,
        amount_alt: b.amount,
        amount_neu,
        delta: amount_neu - b.amount,
        pct: b.amount !== 0 ? (amount_neu - b.amount) / Math.abs(b.amount) * 100 : null,
      };
    });
  }
  // Classic
  const vol = lev?.volumen ?? 1;
  const eff = lev?.effizienz ?? 1;
  const effFactor = Math.max(0, 2 - eff);
  return buckets.map(b => {
    const isVar = b.classification === 'variabel';
    const amount_neu = isVar ? b.amount * vol * effFactor : b.amount;
    return {
      ...b,
      amount_alt: b.amount,
      amount_neu,
      delta: amount_neu - b.amount,
      pct: b.amount !== 0 ? (amount_neu - b.amount) / Math.abs(b.amount) * 100 : null,
    };
  });
}

function applyReduktionToBuckets(buckets, reduktion) {
  if (!Array.isArray(buckets)) return [];
  const fac = 1 - (reduktion || 0);
  return buckets.map(b => {
    const amount_neu = b.amount * fac;
    return {
      ...b,
      amount_alt: b.amount,
      amount_neu,
      delta: amount_neu - b.amount,
      pct: b.amount !== 0 ? (amount_neu - b.amount) / Math.abs(b.amount) * 100 : null,
    };
  });
}

/* Mechanik-Texte für die i-Icon-Popups */
const LEVER_INFO = {
  // Classic
  volumen: 'Skaliert Umsatz UND variable Kosten proportional. Fixe Kosten (Halle, Leasing, Versicherung) bleiben konstant → der Deckungsbeitrag wächst überproportional. Das ist die Hebelwirkung.',
  preis: 'Erhöht oder senkt nur den Umsatz (Tarif-Änderung). Kosten bleiben gleich → wirkt 1:1 auf den DB.',
  effizienz: 'Reduziert die variablen Stück-Kosten (z.B. schnelleres Picking) OHNE Volumenänderung. Formel: var-Kosten × (2 − Effizienz). 1.0 = neutral, 1.5 = 50 % schneller, 0.7 = 30 % langsamer.',
  reduktion: 'Pauschale Senkung des Gemeinkosten-Blocks (Verwaltung / MA-Verpflegung / Support / Lageropt.) — wirkt direkt aufs Gesamt-EBITDA, nicht auf einzelne Segmente.',
  // Sprungfix
  volumen_at: 'Skaliert AT-Volumen (Pakete/Vorgänge). Personal reagiert SPRUNGFIX in 20h/Woche-Stufen — wenn das benötigte Volumen die Kapazität übersteigt, kommt eine neue Stufe (0,5 VZÄ, default 1.600 €/Monat) dazu. AT-Umsatz und sonstige variable AT-Kosten skalieren linear.',
  volumen_de: 'Skaliert NUR den DE-RevShare-Pfad (Finecom 5720) LINEAR. Mehr DE-Volumen → proportional mehr DE-Umsatz und mehr Finecom-Aufwand. AT-Personalstufen werden NICHT angefasst.',
  preis_sprungfix: 'Hebt oder senkt das Tarif-Niveau für AT UND DE. Kosten bleiben gleich → 1:1 auf den DB.',
  effizienz_sprungfix: 'Verschiebt die Kapazität pro Kraft. 1.2 = jede 0,5-VZÄ-Stufe schafft 20 % mehr → die Basis-Belegschaft hält mehr Volumen ab, bevor eine neue Stufe gebucht werden muss. KEIN direkter Kosten-Multiplikator, sondern Kapazitäts-Hebel im Stufen-Rechner.',
};

export default function InvestorScenario() {
  const [searchParams] = useSearchParams();
  const { user } = useInvestorAuth();
  const isAdmin = user?.role === 'admin';

  const [periodState, setPeriodState] = useState(() => {
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });

  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [levers, setLevers] = useState({});
  const [gemeinReduktion, setGemeinReduktion] = useState(GEMEIN_DEFAULT_REDUKTION);
  const [editorOpen, setEditorOpen] = useState(false);

  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/scenario/baseline?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setBaseline(null); }
        else {
          setBaseline(json);
          setLevers(buildEmptyLevers(json.segments || []));
          setGemeinReduktion(GEMEIN_DEFAULT_REDUKTION);
        }
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodQuery]);

  const setLever = useCallback((segCode, key, value) => {
    setLevers(prev => {
      const seg = baseline?.segments?.find(s => s.code === segCode);
      const def = seg ? defaultLeversForSegment(seg) : CLASSIC_DEFAULT_LEVERS;
      return { ...prev, [segCode]: { ...(prev[segCode] || def), [key]: value } };
    });
  }, [baseline]);

  const resetAll = useCallback(() => {
    if (!baseline) return;
    setLevers(buildEmptyLevers(baseline.segments || []));
    setGemeinReduktion(GEMEIN_DEFAULT_REDUKTION);
  }, [baseline]);

  // Szenario-Rechnung
  const scenario = useMemo(() => {
    if (!baseline) return null;
    const segRows = baseline.segments.map(s => {
      const sprungfix = !!s.sprungfix_enabled;
      const def = defaultLeversForSegment(s);
      const lev = levers[s.code] || def;
      const calc = applyLevers(s, lev, s.personnel_config);
      const segWithCalc = { ...s, calc };
      const bucketsScenario = applyLeversToBuckets(s.buckets || [], lev, segWithCalc);
      const umsatzPct = s.umsatz !== 0 ? (calc.umsatz_neu - s.umsatz) / Math.abs(s.umsatz) * 100 : null;
      return {
        code: s.code, name: s.name,
        sprungfix_enabled: sprungfix,
        personnel_config: s.personnel_config || null,
        // Baseline
        umsatz_alt: s.umsatz, umsatz_at_alt: s.umsatz_at || 0, umsatz_de_alt: s.umsatz_de || 0,
        fix_alt: s.aufwand_fix, var_alt: s.aufwand_variabel,
        aufw_alt: s.aufwand_total, db_alt: s.db,
        aufwand_personal_at: s.aufwand_personal_at || 0,
        aufwand_finecom_de:  s.aufwand_finecom_de  || 0,
        aufwand_normal_var:  s.aufwand_normal_var  || 0,
        aufwand_normal_fix:  s.aufwand_normal_fix  || 0,
        // Szenario
        ...calc,
        umsatz_pct: umsatzPct,
        // Levers
        lever: lev,
        // Buckets
        buckets: bucketsScenario,
      };
    });
    const sigmaUmsatzAlt = segRows.reduce((a, x) => a + x.umsatz_alt, 0);
    const sigmaUmsatzNeu = segRows.reduce((a, x) => a + x.umsatz_neu, 0);
    const sigmaDbAlt     = segRows.reduce((a, x) => a + x.db_alt, 0);
    const sigmaDbNeu     = segRows.reduce((a, x) => a + x.db_neu, 0);
    const gemAlt         = baseline.gemeinkosten.aufwand_total;
    const gemNeu         = gemAlt * (1 - gemeinReduktion);
    const gemBuckets     = applyReduktionToBuckets(baseline.gemeinkosten.buckets || [], gemeinReduktion);
    const ebitdaAlt      = sigmaDbAlt + gemAlt;
    const ebitdaNeu      = sigmaDbNeu + gemNeu;
    return {
      segments: segRows,
      sigma_umsatz_alt: sigmaUmsatzAlt, sigma_umsatz_neu: sigmaUmsatzNeu,
      sigma_db_alt: sigmaDbAlt,         sigma_db_neu: sigmaDbNeu,
      gemein_alt: gemAlt,               gemein_neu: gemNeu,
      gemein_buckets: gemBuckets,
      ebitda_alt: ebitdaAlt,            ebitda_neu: ebitdaNeu,
      ebitda_diff: ebitdaNeu - ebitdaAlt,
      marge_alt: sigmaUmsatzAlt ? ebitdaAlt / sigmaUmsatzAlt * 100 : null,
      marge_neu: sigmaUmsatzNeu ? ebitdaNeu / sigmaUmsatzNeu * 100 : null,
    };
  }, [baseline, levers, gemeinReduktion]);

  // Wasserfall
  const waterfallData = useMemo(() => {
    if (!scenario) return [];
    const rows = [];
    let cum = 0;
    for (const s of scenario.segments) {
      const start = cum;
      cum += s.db_neu;
      rows.push({
        name: s.name, kind: 'segment',
        base: s.db_neu >= 0 ? start : cum,
        delta: Math.abs(s.db_neu),
        signed: s.db_neu,
      });
    }
    {
      const start = cum;
      cum += scenario.gemein_neu;
      rows.push({
        name: 'Gemeinkosten', kind: 'gemein',
        base: scenario.gemein_neu >= 0 ? start : cum,
        delta: Math.abs(scenario.gemein_neu),
        signed: scenario.gemein_neu,
      });
    }
    rows.push({
      name: 'EBITDA (Szenario)', kind: 'total',
      base: 0, delta: Math.abs(scenario.ebitda_neu), signed: scenario.ebitda_neu,
    });
    return rows;
  }, [scenario]);

  const isUnchanged = useMemo(() => {
    if (!scenario) return true;
    if (gemeinReduktion !== 0) return false;
    return scenario.segments.every(s => isLeverNeutral(s.lever, s.sprungfix_enabled));
  }, [scenario, gemeinReduktion]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">Szenario-Rechner</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Live-Simulator: drehe an Volumen / Preis / Effizienz pro Segment + Gemeinkosten-Hebel —
            EBITDA-Hebelwirkung sofort sichtbar. Read-only auf Baseline-Daten, Rechnung im Browser.
          </div>
        </div>
        <PeriodToggle
          period={periodState.period} year={periodState.year} month={periodState.month}
          quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">Fehler: {error}</div>
      )}

      {/* Vorher / Nachher KPI-Block */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <CompareKpiTile label="Σ Umsatz"  alt={scenario?.sigma_umsatz_alt} neu={scenario?.sigma_umsatz_neu}
          loading={loading} Icon={TrendingUp} iconColor="#338edc" iconTint="#EBF5FD" />
        <CompareKpiTile label="Σ Segment-DB" alt={scenario?.sigma_db_alt} neu={scenario?.sigma_db_neu}
          loading={loading} Icon={Calculator} iconColor="#10B981" iconTint="#D1FAE5" />
        <CompareKpiTile label="Gemeinkosten" alt={scenario?.gemein_alt} neu={scenario?.gemein_neu}
          loading={loading} Icon={Wallet} iconColor="#EF4444" iconTint="#FEE2E2"
          subAlt={gemeinReduktion === 0 ? null : `−${(gemeinReduktion*100).toFixed(0)} %`} />
        <CompareKpiTile label="EBITDA"
          alt={scenario?.ebitda_alt} neu={scenario?.ebitda_neu}
          loading={loading}
          Icon={scenario && scenario.ebitda_diff >= 0 ? TrendingUp : TrendingDown}
          iconColor={scenario && scenario.ebitda_diff >= 0 ? '#10B981' : '#EF4444'}
          iconTint={scenario && scenario.ebitda_diff >= 0 ? '#D1FAE5' : '#FEE2E2'}
          highlight
          subNeu={scenario?.marge_neu != null ? `Marge ${fmtPct(scenario.marge_neu)}` : null} />
      </div>

      {/* Steuer-Card mit Reset-Button */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sliders width={16} height={16} className="text-[#338edc]" />
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Stellschrauben</h2>
            {!isUnchanged && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#338edc]/10 text-[#338edc] font-medium uppercase tracking-wider">live</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                type="button" onClick={() => setEditorOpen(true)}
                className="text-xs px-2.5 py-1.5 rounded-md border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FC] inline-flex items-center gap-1.5"
              >
                <Settings width={13} height={13} /> Fix/Variabel-Klassifikation
              </button>
            )}
            <button
              type="button" onClick={resetAll} disabled={isUnchanged}
              className="text-xs px-2.5 py-1.5 rounded-md border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FC] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <RotateCcw width={13} height={13} /> Zurücksetzen
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {loading && !baseline && (
            <div className="text-sm text-[#9CA3AF] py-6 text-center">Lade Baseline aus Odoo…</div>
          )}
          {scenario?.segments?.map(s => (
            <SegmentLeverRow key={s.code} seg={s} onChange={(k, v) => setLever(s.code, k, v)} />
          ))}
          {scenario && (
            <div className="border-t border-[#E5E7EB] pt-3 mt-3">
              <GemeinLeverRow
                alt={scenario.gemein_alt} neu={scenario.gemein_neu}
                reduktion={gemeinReduktion}
                onChange={setGemeinReduktion}
                lines={baseline?.gemeinkosten?.lines_processed}
                buckets={scenario.gemein_buckets}
              />
            </div>
          )}
        </div>
      </div>

      {/* Wasserfall */}
      {scenario && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">EBITDA-Wasserfall (Szenario)</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Grün = positiver Segment-DB · Rot = negativ / Gemeinkosten · Dunkelblau = EBITDA-Ziel
            </div>
          </div>
          <div className="p-4">
            <div style={{ height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={waterfallData} margin={{ top: 16, right: 16, bottom: 16, left: 16 }}>
                  <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={{ stroke: '#E5E7EB' }} interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: '#6B7280' }} tickLine={false} axisLine={false}
                    tickFormatter={v => new Intl.NumberFormat('de-AT', { notation: 'compact', maximumFractionDigits: 1 }).format(v) + ' €'} />
                  <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12 }}
                    formatter={(_, __, p) => fmtEurDetail(p.payload.signed)}
                    labelFormatter={l => l}
                  />
                  <Bar dataKey="base" stackId="wf" fill="transparent" />
                  <Bar dataKey="delta" stackId="wf">
                    {waterfallData.map((d, i) => {
                      let fill = '#10B981';
                      if (d.kind === 'segment') fill = d.signed >= 0 ? '#10B981' : '#EF4444';
                      else if (d.kind === 'gemein') fill = '#EF4444';
                      else if (d.kind === 'total') fill = d.signed >= 0 ? '#1A1A2E' : '#7F1D1D';
                      return <Cell key={i} fill={fill} />;
                    })}
                    <LabelList
                      dataKey="signed"
                      position="top"
                      formatter={v => fmtEurDetail(v)}
                      style={{ fontSize: 11, fill: '#1A1A2E', fontWeight: 500 }}
                    />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Hebelwirkungs-Tabelle */}
      {scenario && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="text-sm font-semibold text-[#1A1A2E]">Hebelwirkung pro Segment</h2>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Fix-Aufwand skaliert <strong>nicht</strong> mit Volumen — VGP-Miete bleibt, Halle füllen lassen Deckungsgrad explodieren.
              Outbound + Inbound nutzen <strong>Sprungfix-Personalmodell</strong> (20h/W-Stufen).
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left  px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Segment</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Umsatz</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Aufw. fix</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">Aufw. var.</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider">DB</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">DB-Marge</th>
                  <th className="text-right px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider w-24">Δ DB</th>
                </tr>
              </thead>
              <tbody>
                {scenario.segments.map(s => {
                  const dbDelta = s.db_neu - s.db_alt;
                  const positive = dbDelta >= 0;
                  return (
                    <tr key={s.code} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]">
                      <td className="px-3 py-2 text-[#1A1A2E]">
                        {s.name}
                        {s.sprungfix_enabled && (
                          <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#EEF2FF] text-[#4F46E5] px-1 py-0.5 rounded">Sprungfix</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="text-[#1A1A2E] font-medium">{fmtEurDetail(s.umsatz_neu)}</div>
                        <div className="text-[10px] text-[#9CA3AF]">war {fmtEurDetail(s.umsatz_alt)}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">
                        {fmtEurDetail(s.fix_alt)}
                        <div className="text-[10px] text-[#9CA3AF]">unverändert</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="text-[#1A1A2E]">{fmtEurDetail(s.var_neu)}</div>
                        <div className="text-[10px] text-[#9CA3AF]">war {fmtEurDetail(s.var_alt)}</div>
                        {s.sprungfix_enabled && s.sprungfix?.stufen > 0 && (
                          <div className="text-[10px] text-[#4F46E5] font-medium">+{s.sprungfix.stufen} Stufen</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="text-[#1A1A2E] font-semibold">{fmtEurDetail(s.db_neu)}</div>
                        <div className="text-[10px] text-[#9CA3AF]">war {fmtEurDetail(s.db_alt)}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">
                        {fmtPct(s.db_marge_neu)}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
                        {fmtSignedEur(dbDelta)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-[#F8F9FC] font-semibold border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 text-[#1A1A2E]">Σ Segmente</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(scenario.sigma_umsatz_neu)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]" colSpan={2}>–</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(scenario.sigma_db_neu)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#4B5563]">
                    {scenario.sigma_umsatz_neu ? fmtPct(scenario.sigma_db_neu / scenario.sigma_umsatz_neu * 100) : '–'}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${scenario.sigma_db_neu - scenario.sigma_db_alt >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {fmtSignedEur(scenario.sigma_db_neu - scenario.sigma_db_alt)}
                  </td>
                </tr>
                <tr className="border-t border-[#E5E7EB]">
                  <td className="px-3 py-2 text-[#4B5563]">−  Gemeinkosten</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#9CA3AF]" colSpan={3}>
                    {gemeinReduktion === 0 ? 'unverändert' : `−${(gemeinReduktion*100).toFixed(0)} % auf Block`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1A1A2E]">{fmtEurDetail(scenario.gemein_neu)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#9CA3AF]">–</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${scenario.gemein_neu - scenario.gemein_alt >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {fmtSignedEur(scenario.gemein_neu - scenario.gemein_alt)}
                  </td>
                </tr>
                <tr className="bg-[#1A1A2E] text-white font-semibold border-t border-[#E5E7EB]">
                  <td className="px-3 py-2">= EBITDA</td>
                  <td className="px-3 py-2 text-right tabular-nums" colSpan={4}>
                    {fmtEurDetail(scenario.ebitda_neu)}
                    <span className="text-[#9CA3AF] text-[10px] ml-2">war {fmtEurDetail(scenario.ebitda_alt)}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtPct(scenario.marge_neu)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${scenario.ebitda_diff >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                    {fmtSignedEur(scenario.ebitda_diff)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer-Info */}
      {baseline && (
        <div className="text-xs text-[#9CA3AF] space-y-1">
          <div>
            Klassifikation v{baseline.classification.version} · {baseline.classification.override_count} Overrides ·
            Defaults: Klasse 5 (Material/Personal) → variabel, Klasse 6/7/8 → fix, sonst fix.
            {baseline.classification.note && <> · {baseline.classification.note}</>}
          </div>
          {baseline.personnel_model && (
            <div>
              Personal-Modell v{baseline.personnel_model.version} (#1194) ·
              Sprungfix-Konten: <code className="text-[#4B5563]">{(baseline.personnel_model.personal_konto_codes || []).join(', ')}</code> ·
              Finecom-Konten: <code className="text-[#4B5563]">{(baseline.personnel_model.finecom_konto_codes || []).join(', ')}</code> ·
              Annahmen editierbar auf der Annahmen-Seite.
            </div>
          )}
          <div>
            Quelle: Odoo <code>account.analytic.line</code> über 5 Segment-KSt + Gemein-KSt [6, 7, 8, 45].
          </div>
        </div>
      )}

      {/* Klassifikations-Editor */}
      {editorOpen && isAdmin && (
        <ClassificationEditor
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            setEditorOpen(false);
            setPeriodState(p => ({ ...p }));
          }}
        />
      )}
    </div>
  );
}

/* ─── Komponenten ──────────────────────────────────────────────────────────── */

function CompareKpiTile({ label, alt, neu, loading, Icon, iconColor, iconTint, highlight, subNeu, subAlt }) {
  const diff = (alt != null && neu != null) ? neu - alt : null;
  const pct  = (alt != null && alt !== 0 && diff != null) ? (diff / Math.abs(alt)) * 100 : null;
  const positive = diff != null && diff >= 0;
  return (
    <div className={`bg-white border rounded-xl p-4 ${highlight ? 'border-[#338edc] ring-1 ring-[#338edc]/20' : 'border-[#E5E7EB]'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
        {Icon && (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ backgroundColor: iconTint || '#F3F4F6' }} aria-hidden="true">
            <Icon width={16} height={16} stroke={iconColor || '#6B7280'} strokeWidth={2} fill="none" />
          </span>
        )}
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums">
        {loading ? <span className="inline-block w-24 h-7 rounded bg-[#F3F4F6] animate-pulse" /> : fmtEur(neu)}
      </div>
      {subNeu && <div className="text-[11px] text-[#9CA3AF] mt-0.5">{subNeu}</div>}
      <div className="text-[11px] text-[#9CA3AF] mt-2 flex items-center justify-between border-t border-[#F3F4F6] pt-2">
        <span>war <span className="text-[#6B7280] tabular-nums">{fmtEur(alt)}</span>
          {subAlt && <span className="ml-1 text-[#338edc]">{subAlt}</span>}
        </span>
        {diff != null && diff !== 0 && (
          <span className={`tabular-nums font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
            {fmtSignedEur(diff)} {pct != null && <span className="text-[10px]">({pct >= 0 ? '+' : ''}{pct.toFixed(0)}%)</span>}
          </span>
        )}
      </div>
    </div>
  );
}

function SegmentLeverRow({ seg, onChange }) {
  const dbDelta = seg.db_neu - seg.db_alt;
  const positive = dbDelta >= 0;
  const isNeutral = isLeverNeutral(seg.lever, seg.sprungfix_enabled);
  const [forceOpen, setForceOpen] = useState(false);
  const open = forceOpen || !isNeutral;
  const sprungfix = seg.sprungfix_enabled;
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-3 hover:border-[#338edc]/50 transition-colors">
      <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#1A1A2E]">{seg.name}</span>
          {sprungfix && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded">
              <Users width={10} height={10} /> Sprungfix
            </span>
          )}
          <span className="text-[10px] text-[#9CA3AF]">
            {sprungfix
              ? <>AT {fmtEur(seg.umsatz_at_alt)} · DE {fmtEur(seg.umsatz_de_alt)}</>
              : <>(fix {fmtEur(seg.fix_alt)} · var {fmtEur(seg.var_alt)})</>}
          </span>
        </div>
        <div className="text-xs tabular-nums flex items-center gap-2">
          <span className="text-[#6B7280]">DB:</span>
          <span className="text-[#1A1A2E] font-medium">{fmtEur(seg.db_neu)}</span>
          {dbDelta !== 0 && (
            <span className={`font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
              {fmtSignedEur(dbDelta)}
            </span>
          )}
          {sprungfix && seg.sprungfix?.stufen > 0 && (
            <span className="text-[10px] text-[#4F46E5] font-medium bg-[#EEF2FF] px-1.5 py-0.5 rounded">
              +{seg.sprungfix.stufen} Stufen
            </span>
          )}
        </div>
      </div>
      {sprungfix ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Slider label="Volumen AT" min={0.5} max={3.0} step={0.05} value={seg.lever.volumen_at}
            onChange={v => onChange('volumen_at', v)} explain="AT-Volumen → Personal-Sprungfix + var. AT-Kosten"
            infoText={LEVER_INFO.volumen_at} accent="indigo" />
          <Slider label="Volumen DE" min={0.5} max={3.0} step={0.05} value={seg.lever.volumen_de}
            onChange={v => onChange('volumen_de', v)} explain="DE-Volumen → Umsatz + Finecom 5720 linear"
            infoText={LEVER_INFO.volumen_de} accent="cyan" />
          <Slider label="Preis" min={0.7} max={1.5} step={0.01} value={seg.lever.preis}
            onChange={v => onChange('preis', v)} explain="Tarif AT+DE (Umsatz × Preis)"
            infoText={LEVER_INFO.preis_sprungfix} />
          <Slider label="Effizienz" min={0.7} max={1.5} step={0.01} value={seg.lever.effizienz}
            onChange={v => onChange('effizienz', v)} explain="Kapazität pro Kraft (1.2 = +20% Output / 0,5-VZÄ)"
            infoText={LEVER_INFO.effizienz_sprungfix} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Slider label="Volumen" min={0.5} max={3.0} step={0.05} value={seg.lever.volumen}
            onChange={v => onChange('volumen', v)} explain="Skaliert Umsatz + variable Kosten"
            infoText={LEVER_INFO.volumen} />
          <Slider label="Preis" min={0.7} max={1.5} step={0.01} value={seg.lever.preis}
            onChange={v => onChange('preis', v)} explain="Nur Umsatz (Tarif-Erhöhung / -Senkung)"
            infoText={LEVER_INFO.preis} />
          <Slider label="Effizienz" min={0.7} max={1.5} step={0.01} value={seg.lever.effizienz}
            onChange={v => onChange('effizienz', v)} explain="Reduziert variable Stk-Kosten (1.0 = neutral)"
            infoText={LEVER_INFO.effizienz} />
        </div>
      )}

      {/* Aufklappbarer Breakdown */}
      <div className="mt-3 -mx-3 -mb-3 border-t border-[#E5E7EB]">
        <button
          type="button"
          onClick={() => setForceOpen(o => !o)}
          className="w-full px-3 py-2 flex items-center justify-between text-[11px] font-medium text-[#4B5563] hover:bg-[#F8F9FC] transition-colors rounded-b-lg"
        >
          <span className="inline-flex items-center gap-1.5">
            {open ? <ChevronUp width={13} height={13} /> : <ChevronDown width={13} height={13} />}
            Was ändert sich?
            {isNeutral && <span className="text-[10px] text-[#9CA3AF] font-normal">(noch nichts verstellt)</span>}
          </span>
          {!isNeutral && (
            <span className="text-[10px] text-[#338edc] uppercase tracking-wider">aktiv</span>
          )}
        </button>
        {open && (
          sprungfix
            ? <SprungfixBreakdownPanel seg={seg} isNeutral={isNeutral} />
            : <BreakdownPanel
                umsatzAlt={seg.umsatz_alt}
                umsatzNeu={seg.umsatz_neu}
                umsatzPct={seg.umsatz_pct}
                buckets={seg.buckets}
                dbAlt={seg.db_alt}
                dbNeu={seg.db_neu}
                isNeutral={isNeutral}
              />
        )}
      </div>
    </div>
  );
}

function GemeinLeverRow({ alt, neu, reduktion, onChange, lines, buckets }) {
  const [forceOpen, setForceOpen] = useState(false);
  const isNeutral = reduktion === 0;
  const open = forceOpen || !isNeutral;
  return (
    <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#F8F9FC]">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#1A1A2E]">Gemeinkosten-Block</span>
          <span className="text-[10px] text-[#9CA3AF]">
            (Verwaltung / MA-Verpflegung / Support / Lageropt. · {lines || 0} Buchungen)
          </span>
        </div>
        <div className="text-xs tabular-nums flex items-center gap-2">
          <span className="text-[#1A1A2E] font-medium">{fmtEur(neu)}</span>
          {reduktion !== 0 && (
            <span className="text-emerald-700 font-medium">
              {fmtSignedEur(neu - alt)}
            </span>
          )}
        </div>
      </div>
      <Slider
        label="Reduktion"
        min={0} max={0.50} step={0.01} value={reduktion}
        onChange={onChange}
        formatValue={v => `${(v*100).toFixed(0)} %`}
        explain="Pauschale Senkung des Gemein-Blocks (0–50 %)"
        infoText={LEVER_INFO.reduktion}
      />

      <div className="mt-3 -mx-3 -mb-3 border-t border-[#E5E7EB]">
        <button
          type="button"
          onClick={() => setForceOpen(o => !o)}
          className="w-full px-3 py-2 flex items-center justify-between text-[11px] font-medium text-[#4B5563] hover:bg-white transition-colors rounded-b-lg"
        >
          <span className="inline-flex items-center gap-1.5">
            {open ? <ChevronUp width={13} height={13} /> : <ChevronDown width={13} height={13} />}
            Was ändert sich?
            {isNeutral && <span className="text-[10px] text-[#9CA3AF] font-normal">(noch nichts verstellt)</span>}
          </span>
          {!isNeutral && (
            <span className="text-[10px] text-[#338edc] uppercase tracking-wider">aktiv</span>
          )}
        </button>
        {open && (
          <BreakdownPanel
            umsatzAlt={null}
            umsatzNeu={null}
            buckets={buckets}
            dbAlt={alt}
            dbNeu={neu}
            isNeutral={isNeutral}
            dbLabel="Gemeinkosten-Block"
          />
        )}
      </div>
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, explain, formatValue, infoText, accent }) {
  const display = formatValue ? formatValue(value) : fmtMult(value);
  const isNeutral = (value === 1.0 && !formatValue) || (value === 0 && formatValue);
  const activeColor = accent === 'indigo' ? '#4F46E5' : (accent === 'cyan' ? '#0891B2' : '#338edc');
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="inline-flex items-center gap-1">
          <span className="text-[11px] font-medium text-[#4B5563]">{label}</span>
          {infoText && <InfoPopover text={infoText} title={label} />}
        </div>
        <span
          className={`text-xs tabular-nums font-mono ${isNeutral ? 'text-[#9CA3AF]' : 'font-semibold'}`}
          style={isNeutral ? null : { color: activeColor }}
        >
          {display}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-[#E5E7EB]"
        style={{ accentColor: activeColor }}
        aria-label={label}
      />
      {explain && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{explain}</div>}
    </div>
  );
}

function InfoPopover({ text, title }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex items-center">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors ${open ? 'text-[#338edc] bg-[#338edc]/10' : 'text-[#9CA3AF] hover:text-[#338edc]'}`}
        aria-label={`Mechanik erklären: ${title}`}
        aria-expanded={open}
      >
        <Info width={11} height={11} strokeWidth={2.5} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-full mt-1.5 z-30 w-72 bg-white border border-[#E5E7EB] rounded-lg shadow-lg p-3 text-[11px] leading-snug text-[#4B5563]"
        >
          <div className="font-semibold text-[#1A1A2E] mb-1 text-[11px]">{title} — Mechanik</div>
          {text}
        </div>
      )}
    </span>
  );
}

/* Classic BreakdownPanel: Basis→Szenario je Kostenposten + DB-Zeile */
function BreakdownPanel({ umsatzAlt, umsatzNeu, umsatzPct, buckets, dbAlt, dbNeu, isNeutral, dbLabel }) {
  const dbDelta = (dbNeu ?? 0) - (dbAlt ?? 0);
  const dbPositive = dbDelta >= 0;
  const showUmsatz = umsatzAlt != null && umsatzNeu != null;

  const varBuckets = (buckets || []).filter(b => b.classification === 'variabel');
  const fixBuckets = (buckets || []).filter(b => b.classification !== 'variabel');
  const TOP_N = 8;
  const varVisible = varBuckets.slice(0, TOP_N);
  const varHidden  = varBuckets.length - varVisible.length;
  const fixVisible = fixBuckets.slice(0, TOP_N);
  const fixHidden  = fixBuckets.length - fixVisible.length;

  return (
    <div className="px-3 py-3 bg-[#F8F9FC] rounded-b-lg space-y-2.5">
      {showUmsatz && (
        <BreakdownRow
          label="Umsatz"
          alt={umsatzAlt}
          neu={umsatzNeu}
          pct={umsatzPct}
          colorDelta={(umsatzNeu - umsatzAlt) >= 0 ? 'emerald' : 'red'}
          bold
        />
      )}

      {varVisible.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-medium">
            Variable Kosten {isNeutral ? '' : '(skalieren mit Volumen × (2 − Effizienz))'}
          </div>
          <div className="space-y-1">
            {varVisible.map((b, i) => (
              <BucketRow key={`v${i}`} b={b} isNeutral={isNeutral} />
            ))}
            {varHidden > 0 && (
              <div className="text-[10px] text-[#9CA3AF] pl-2">… und {varHidden} weitere variable Posten</div>
            )}
          </div>
        </div>
      )}

      {fixVisible.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-medium">
            Fixe Kosten <span className="text-[#9CA3AF] normal-case font-normal">— skalieren NICHT, bleiben konstant</span>
          </div>
          <div className="space-y-1">
            {fixVisible.map((b, i) => (
              <BucketRow key={`f${i}`} b={b} isNeutral={isNeutral} isFix />
            ))}
            {fixHidden > 0 && (
              <div className="text-[10px] text-[#9CA3AF] pl-2">… und {fixHidden} weitere fixe Posten</div>
            )}
          </div>
        </div>
      )}

      {(!varVisible.length && !fixVisible.length) && (
        <div className="text-[11px] text-[#9CA3AF] italic">Keine Kosten-Posten für diesen Zeitraum.</div>
      )}

      <div className="pt-2 border-t border-[#E5E7EB]">
        <BreakdownRow
          label={dbLabel || 'DB'}
          alt={dbAlt}
          neu={dbNeu}
          pct={dbAlt !== 0 ? (dbDelta / Math.abs(dbAlt)) * 100 : null}
          colorDelta={dbPositive ? 'emerald' : 'red'}
          bold
          showDeltaAbs
        />
      </div>
    </div>
  );
}

/* #1194 — Sprungfix-BreakdownPanel: AT/DE-Umsatz, Stufen-Zeile, sub-klassifizierte Aufwände */
function SprungfixBreakdownPanel({ seg, isNeutral }) {
  const sf = seg.sprungfix;
  if (!sf) return null;
  const personalBuckets = (seg.buckets || []).filter(b => b.subclassification === 'personal_at');
  const finecomBuckets  = (seg.buckets || []).filter(b => b.subclassification === 'finecom_de');
  const normalVarBuckets= (seg.buckets || []).filter(b => b.subclassification === 'normal_var');
  const normalFixBuckets= (seg.buckets || []).filter(b => b.subclassification === 'normal_fix' || (b.classification === 'fix' && !b.subclassification));
  const TOP_N = 6;

  const dbDelta = seg.db_neu - seg.db_alt;
  const dbPositive = dbDelta >= 0;
  const einheit = sf.einheit || '';
  const stufenLabel = sf.stufen === 1 ? '1 Stufe' : `${sf.stufen} Stufen`;
  const benoetigtPct = sf.basis_kapazitaet ? ((seg.lever.volumen_at - 1) * 100) : 0;
  const vorhandenPct = ((seg.lever.effizienz - 1) * 100);

  return (
    <div className="px-3 py-3 bg-[#F8F9FC] rounded-b-lg space-y-3">
      {/* AT-/DE-Umsatz */}
      <div className="grid grid-cols-2 gap-2">
        <BreakdownRow
          label="Umsatz AT"
          alt={sf.umsatz_at_alt}
          neu={seg.umsatz_at_neu}
          pct={sf.umsatz_at_alt !== 0 ? (seg.umsatz_at_neu - sf.umsatz_at_alt) / Math.abs(sf.umsatz_at_alt) * 100 : null}
          colorDelta={(seg.umsatz_at_neu - sf.umsatz_at_alt) >= 0 ? 'emerald' : 'red'}
        />
        <BreakdownRow
          label="Umsatz DE"
          alt={sf.umsatz_de_alt}
          neu={seg.umsatz_de_neu}
          pct={sf.umsatz_de_alt !== 0 ? (seg.umsatz_de_neu - sf.umsatz_de_alt) / Math.abs(sf.umsatz_de_alt) * 100 : null}
          colorDelta={(seg.umsatz_de_neu - sf.umsatz_de_alt) >= 0 ? 'emerald' : 'red'}
        />
      </div>

      {/* Sprungfix-Stufen-Anzeige */}
      <div className="bg-white border border-[#EEF2FF] rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <Users width={14} height={14} className="text-[#4F46E5]" />
          <span className="text-xs font-semibold text-[#1A1A2E]">Personal (AT, Sprungfix)</span>
          {sf.stufen > 0 && (
            <span className="text-[10px] uppercase tracking-wider bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded font-medium">
              +{stufenLabel}
            </span>
          )}
          {sf.stufen === 0 && !isNeutral && (
            <span className="text-[10px] text-[#6B7280]">keine neue Stufe nötig</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] tabular-nums">
          <div className="text-[#6B7280]">Benötigt (AT-Volumen)</div>
          <div className="text-right text-[#1A1A2E]">
            {fmtNum(sf.benoetigt)} {einheit}
            {benoetigtPct !== 0 && (
              <span className={`ml-1 text-[10px] ${benoetigtPct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                ({benoetigtPct >= 0 ? '+' : ''}{benoetigtPct.toFixed(0)}%)
              </span>
            )}
          </div>
          <div className="text-[#6B7280]">Vorhanden (Basis × Effizienz)</div>
          <div className="text-right text-[#1A1A2E]">
            {fmtNum(sf.vorhanden)} {einheit}
            {vorhandenPct !== 0 && (
              <span className={`ml-1 text-[10px] ${vorhandenPct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                ({vorhandenPct >= 0 ? '+' : ''}{vorhandenPct.toFixed(0)}%)
              </span>
            )}
          </div>
          <div className="text-[#6B7280]">Kapazität pro Stufe</div>
          <div className="text-right text-[#4B5563]">{fmtNum(sf.kapazitaet_pro_stufe)} {einheit}</div>
          {sf.stufen > 0 && (
            <>
              <div className="text-[#4F46E5] font-medium pt-1 border-t border-[#F1F2F4]">
                → {stufenLabel} (je 20h/W) × {fmtEur(sf.kosten_pro_stufe_eur)}
              </div>
              <div className="text-right text-[#4F46E5] font-semibold pt-1 border-t border-[#F1F2F4]">
                +{fmtEur(sf.personalkosten_zusatz)} Personalkosten
              </div>
            </>
          )}
        </div>
        {personalBuckets.length > 0 && (
          <div className="mt-2 pt-2 border-t border-[#F1F2F4] space-y-0.5">
            {personalBuckets.slice(0, TOP_N).map((b, i) => (
              <BucketRow key={`p${i}`} b={b} isNeutral={isNeutral} />
            ))}
            {personalBuckets.length > TOP_N && (
              <div className="text-[10px] text-[#9CA3AF] pl-2">… und {personalBuckets.length - TOP_N} weitere Personal-Konten</div>
            )}
          </div>
        )}
      </div>

      {/* Finecom DE */}
      {finecomBuckets.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-medium">
            Finecom DE (linear mit Volumen DE)
          </div>
          <div className="space-y-1">
            {finecomBuckets.slice(0, TOP_N).map((b, i) => (
              <BucketRow key={`fc${i}`} b={b} isNeutral={isNeutral} />
            ))}
          </div>
        </div>
      )}

      {/* Normal-Variable AT */}
      {normalVarBuckets.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-medium">
            Sonstige variable Kosten AT (linear mit Volumen AT)
          </div>
          <div className="space-y-1">
            {normalVarBuckets.slice(0, TOP_N).map((b, i) => (
              <BucketRow key={`nv${i}`} b={b} isNeutral={isNeutral} />
            ))}
            {normalVarBuckets.length > TOP_N && (
              <div className="text-[10px] text-[#9CA3AF] pl-2">… und {normalVarBuckets.length - TOP_N} weitere</div>
            )}
          </div>
        </div>
      )}

      {/* Normal-Fix */}
      {normalFixBuckets.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-medium">
            Fixe Kosten <span className="text-[#9CA3AF] normal-case font-normal">— skalieren NICHT</span>
          </div>
          <div className="space-y-1">
            {normalFixBuckets.slice(0, TOP_N).map((b, i) => (
              <BucketRow key={`nf${i}`} b={b} isNeutral={isNeutral} isFix />
            ))}
            {normalFixBuckets.length > TOP_N && (
              <div className="text-[10px] text-[#9CA3AF] pl-2">… und {normalFixBuckets.length - TOP_N} weitere fixe</div>
            )}
          </div>
        </div>
      )}

      {/* DB */}
      <div className="pt-2 border-t border-[#E5E7EB]">
        <BreakdownRow
          label="DB"
          alt={seg.db_alt}
          neu={seg.db_neu}
          pct={seg.db_alt !== 0 ? (dbDelta / Math.abs(seg.db_alt)) * 100 : null}
          colorDelta={dbPositive ? 'emerald' : 'red'}
          bold
          showDeltaAbs
        />
      </div>
    </div>
  );
}

function BreakdownRow({ label, alt, neu, pct, colorDelta, bold, showDeltaAbs }) {
  const delta = (neu ?? 0) - (alt ?? 0);
  const colorClass = colorDelta === 'emerald' ? 'text-emerald-700' : (colorDelta === 'red' ? 'text-red-700' : 'text-[#4B5563]');
  return (
    <div className="flex items-center justify-between text-xs tabular-nums">
      <span className={`${bold ? 'font-semibold text-[#1A1A2E]' : 'text-[#4B5563]'}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-[#9CA3AF]">{fmtEurDetail(alt)}</span>
        <span className="text-[#9CA3AF]">→</span>
        <span className={`${bold ? 'font-semibold text-[#1A1A2E]' : 'text-[#1A1A2E]'} min-w-[80px] text-right`}>
          {fmtEurDetail(neu)}
        </span>
        {delta !== 0 && (
          <span className={`${colorClass} font-medium min-w-[70px] text-right`}>
            {showDeltaAbs ? fmtSignedEur(delta) : (pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%` : '')}
          </span>
        )}
        {delta === 0 && <span className="text-[#9CA3AF] text-[10px] min-w-[70px] text-right">unverändert</span>}
      </div>
    </div>
  );
}

function BucketRow({ b, isNeutral, isFix }) {
  const delta = b.amount_neu - b.amount_alt;
  const changed = !isNeutral && Math.abs(delta) > 0.005;
  const positive = delta < 0;
  return (
    <div className="flex items-center justify-between text-[11px] tabular-nums pl-2 py-0.5 hover:bg-white rounded">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-[#9CA3AF] font-mono text-[10px] shrink-0">{b.code}</span>
        <span className="text-[#4B5563] truncate" title={b.name}>{b.name}</span>
        {isFix && (
          <span className="text-[9px] uppercase tracking-wider bg-[#E5E7EB] text-[#6B7280] px-1 py-0.5 rounded shrink-0">fix</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[#9CA3AF]">{fmtEurDetail(b.amount_alt)}</span>
        <span className="text-[#9CA3AF]">→</span>
        <span className="text-[#1A1A2E] min-w-[80px] text-right">{fmtEurDetail(b.amount_neu)}</span>
        {changed ? (
          <span className={`min-w-[60px] text-right font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
            {b.pct != null ? `${b.pct >= 0 ? '+' : ''}${b.pct.toFixed(0)}%` : ''}
          </span>
        ) : (
          <span className="text-[#9CA3AF] min-w-[60px] text-right text-[10px]">{isFix ? 'fix' : 'unverändert'}</span>
        )}
      </div>
    </div>
  );
}

/* ─── Klassifikations-Editor (admin-only) ──────────────────────────────────── */

function ClassificationEditor({ onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [text, setText] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/investor/scenario/classification`, { credentials: 'include' });
        const json = await res.json();
        setCfg(json);
        setText(JSON.stringify(json, null, 2));
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const parsed = JSON.parse(text);
      const res = await fetch(`${API}/api/investor/scenario/classification`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(parsed),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Speichern fehlgeschlagen');
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-[#E5E7EB] max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-[#1A1A2E]">Fix/Variabel-Klassifikation</div>
            <div className="text-[11px] text-[#9CA3AF] mt-0.5">
              Defaults pro UGB-Kontoklasse + explizite Overrides pro Konto-Code. Cache wird sofort invalidiert.
            </div>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#1A1A2E] text-xl leading-none">×</button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-[#9CA3AF]">Lade…</div>
          ) : (
            <>
              <div className="text-xs text-[#6B7280] mb-2">
                Schema: <code>{`{ version, defaults: { by_class: {"5":"variabel",...}, fallback }, overrides: [{ code, classification, note? }] }`}</code>
              </div>
              <textarea
                value={text} onChange={e => setText(e.target.value)}
                className="w-full font-mono text-[11px] border border-[#E5E7EB] rounded-md p-2 leading-relaxed"
                style={{ minHeight: 360 }}
                spellCheck={false}
              />
              {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#E5E7EB] flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-[#6B7280] hover:text-[#1A1A2E]">Abbrechen</button>
          <button
            onClick={save} disabled={saving || loading}
            className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-60"
          >
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
